'use client';

// Stepper de instalacao: 3 passos com linha vertical centralizada nos numeros,
// indo do badge 1 ao badge 3, com preenchimento no gradiente conforme o scroll.
// Respeita prefers-reduced-motion (linha cheia, sem animar).

import { useEffect, useRef, type ReactNode } from 'react';

interface Step {
  title: string;
  desc: ReactNode;
}

const STEPS: Step[] = [
  {
    title: 'Escolha a Conta de Anúncios e o Pixel',
    desc: (
      <>
        Conecte sua conta Meta e selecione a{' '}
        <b className="text-ink font-semibold">Conta de Anúncios</b> e o{' '}
        <b className="text-ink font-semibold">Pixel</b> que vão receber os eventos.
      </>
    ),
  },
  {
    title: 'Configure o Tracker no seu site',
    desc: (
      <>
        Cole o snippet no header das suas páginas ou rode{' '}
        <b className="text-ink font-semibold">npx</b> na sua página de vendas, com sua IDE favorita.{' '}
        <span className="text-trakd-brand font-semibold">Funciona em qualquer site</span>.
      </>
    ),
  },
  {
    title: 'Conecte o webhook da sua plataforma',
    desc: (
      <>
        Cole-URL na sua plataforma favorita (Hotmart, Kiwify, Shopify e mais). Validação automática.
      </>
    ),
  },
];

export function InstallSteps() {
  const stepsRef = useRef<HTMLDivElement | null>(null);
  const railRef = useRef<HTMLDivElement | null>(null);
  const fillRef = useRef<HTMLDivElement | null>(null);
  const railHRef = useRef(0);

  useEffect(() => {
    const steps = stepsRef.current;
    const rail = railRef.current;
    const fill = fillRef.current;
    if (!steps || !rail || !fill) return;

    const badges = steps.querySelectorAll<HTMLElement>('[data-badge]');
    const first = badges[0];
    const last = badges[badges.length - 1];
    if (!first || !last) return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const place = (): void => {
      const sr = steps.getBoundingClientRect();
      const r1 = first.getBoundingClientRect();
      const r3 = last.getBoundingClientRect();
      const top = r1.top + r1.height / 2 - sr.top;
      const bottom = r3.top + r3.height / 2 - sr.top;
      const h = bottom - top;
      railHRef.current = h;
      rail.style.top = `${top}px`;
      rail.style.height = `${h}px`;
      fill.style.top = `${top}px`;
      if (reduced) fill.style.height = `${h}px`;
    };

    const onScroll = (): void => {
      if (reduced) return;
      const sr = steps.getBoundingClientRect();
      const vh = window.innerHeight;
      const p = Math.max(0, Math.min(1, (vh * 0.78 - sr.top) / (sr.height * 0.7)));
      fill.style.height = `${railHRef.current * p}px`;
    };

    place();
    onScroll();
    const ro = new ResizeObserver(() => {
      place();
      onScroll();
    });
    ro.observe(steps);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      ro.disconnect();
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  return (
    <div>
      <div ref={stepsRef} className="relative">
        <div
          ref={railRef}
          aria-hidden
          className="bg-violet/15 absolute left-[18px] w-0.5 rounded-full"
        />
        <div
          ref={fillRef}
          aria-hidden
          className="absolute left-[18px] h-0 w-0.5 rounded-full bg-[linear-gradient(180deg,#eae7fb,#9061ff)] shadow-[0_0_10px_rgba(144,97,255,0.6)] transition-[height] duration-150 ease-linear"
        />
        {STEPS.map((s, i) => (
          <div key={s.title} className="relative flex gap-4 pb-7 last:pb-0">
            <span
              data-badge
              className="bg-trakd-fill text-on-brand shadow-trakd-glow-violet relative z-[2] grid h-[38px] w-[38px] shrink-0 place-items-center rounded-xl text-[15px] font-bold"
            >
              {i + 1}
            </span>
            <div>
              <h3 className="text-ink mt-[7px] text-base font-semibold">{s.title}</h3>
              <p className="text-ink-muted mt-1 text-sm leading-relaxed">{s.desc}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="border-violet/25 mt-6 rounded-2xl border bg-[linear-gradient(180deg,rgba(144,97,255,0.10),rgba(144,97,255,0.02))] p-4">
        <p className="text-sm leading-relaxed text-[#d6d1e4]">
          <b className="text-ink font-bold">Pronto.</b> A partir daí, todo evento (
          <b className="text-trakd-brand font-semibold">
            Venda, Início de Compra, Cancelamento, Reembolso
          </b>{' '}
          e mais) é disparado pro nosso servidor e pra Meta, persistindo todos os dados de quando o
          usuário acessou sua página.
        </p>
      </div>
    </div>
  );
}

export default InstallSteps;
